/* ---------------------------------------------------------------------
   The Minimalist logo indicator on a case photo, on every page that shows
   one (Oct 2026). The product page (assets/gc-logo-addon.js), the
   Personalized iPhone Case builder and the leather set builder all draw it
   through this one API. The look: assets/gc-logo-spot.css. The words, the
   price and the twin maps: snippets/gc-logo-spot-kit.liquid.

   What it shows (owner, 17 Oct 2026: the pressed state's dashed outline
   "looks bad", "Remove logo" looks bad, "it should be known it's put logo
   on the inside ... Perhaps 'Logo Inside'?"):
   - the gold logo outside (the photo as shot): a light frame on the
     wordmark, the initials spot's family look, and an offer under it:
     "o Logo inside +$25";
   - the logo inside (the photo is its logo-free twin): nothing over the
     clean leather but one small see-through chip where the wordmark was,
     "v Logo inside x". Its glass follows the leather under it, read from
     the photo itself: smoked glass and white words on a dark leather,
     frosted glass and dark words on a light one, so the words read on
     every colour. The whole chip is the way back; the x only says so.
   Each indicator is one toggle button (aria-pressed: the logo is inside),
   named with the words it shows (WCAG 2.5.3), announced once per press.

   API
   GCLogoSpot.mount(host, img, pos, opts) -> handle (null without words)
     host  the element the layer is drawn in: the photo's own wrapper, made
           position:relative if it is static. Inside any slider/scroller.
     img   the <img>. Its picture is followed through object-fit (contain,
           cover, scale-down), object-position, resizes, lazy loading and
           any src/srcset change (a twin swap, another colour's photo).
     pos   {x, y, w, r}: the wordmark's centre and width in % of the
           picture and its tilt in degrees (GCLogo.spot(), the twin map).
     opts  isOn()    true while the logo is inside   default GCLogo.isOn
           toggle()  flip it                          default GCLogo.toggle
           price     "$25" as the page shows it, or a function returning it
                                                      default the kit's
           labels    {inside, aria, on, off}          default the kit's
                     (aria may hold [price]; on/off are the announcements)
           tone      'auto' | 'dark' | 'light'        default 'auto'
           clip      false: never hidden when an overflow:hidden box cuts
                     the wordmark off                 default true
           onToggle(on)  after each press
     handle  el, button
             refresh()   read isOn/price/labels again and refit (call it
                         after your own state or price changes; a GCLogo
                         change refreshes every spot by itself)
             update(pos) the wordmark moved (another photo)
             destroy()   remove the layer and its listeners
   GCLogoSpot.refreshAll()   every spot on the page
   GCLogoSpot.CSS_URL        the stylesheet (from the kit; loaded by mount)

   GCLogo: the page's one choice, "is the logo inside?"
     isOn(), set(on), toggle(), onChange(fn) -> off()   fn(on)
     own({isOn, toggle, tag})  a control on the page holds the state (the
                         product page's row square); it calls
                         GCLogo.changed() after every change
     changed()           tell every listener and spot
     twin(productId?, mediaId)  the logo-free twin's url, or ''
     spot(productId?, mediaId)  {x, y, w, r}, or null
     register(productId, map)   the gc-logo-free-map object; maps in the page
                         as <script type="application/json"
                         data-gc-logo-map="PRODUCT_ID"> are read by themselves
     kit()               {labels, price, fee, css}
     tagItems(items)     a /cart/add.js items array, tagged in place by the
                         owner's tag(items); returns how many cases it tagged
   document 'gc:logo-change' {detail: {on}} after every change.

   Liquid (snippets/gc-logo-spot-kit.liquid):
     {%- render 'gc-logo-spot-kit' -%}   once: words, price, fee variant,
                                         stylesheet, this script (defer)
     {%- render 'gc-logo-spot-kit', map_product: p, map_only: true -%}
                                         per product: its twin map

   A builder, for example (its own state, its own preview photo):
     var pos = GCLogo.spot(productId, mediaId);          // null: no spot
     var spot = pos && GCLogoSpot.mount(previewBox, previewImg, pos, {
       isOn: function () { return state.logoInside; },
       toggle: function () {
         state.logoInside = !state.logoInside;           // the photo follows:
         previewImg.src = state.logoInside ? GCLogo.twin(productId, mediaId) + '&width=1200' : originalSrc;
       }
     });
     another colour: spot.update(GCLogo.spot(productId, newMediaId)); own
     state changed elsewhere: spot.refresh(); gone: spot.destroy().
   In the cart the choice is a pair (assets/gc-logo-sync.js keeps it):
   the case line {"Logo": "Minimalist", "_logo_id": id} and, sent first,
   the add-on line {id: GCLogo.kit().fee, quantity: the case's,
   properties: {"_logo_link": id}}.
   --------------------------------------------------------------------- */
(function () {
  if (window.GCLogoSpot) return;
  var doc = document;
  var MOVE = 10;     /* px between press and release: more is a swipe, and the slider keeps it */
  var WIDEN = 1.12;  /* the frame is this much wider than the wordmark ...                     */
  var ASPECT = 7.5;  /* ... and this flat. gc-logo-spot.css uses the same two numbers.          */
  var SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"';
  var ICON_OFFER = SVG + ' stroke-width="2.2" class="gc-lgs__o"><circle cx="12" cy="12" r="8"/></svg>';
  var ICON_IN = SVG + ' stroke-width="2.6" class="gc-lgs__v"><path d="M5 12.6 9.6 17.2 19 7.8"/></svg>';
  var ICON_BACK = SVG + ' stroke-width="2.4" class="gc-lgs__x"><path d="M7 7l10 10M17 7 7 17"/></svg>';

  /* ---------------------------------------------------------- the kit (snippets/gc-logo-spot-kit.liquid) */
  var KIT = null;
  function kit() {
    if (KIT) return KIT;
    var el = doc.querySelector('script[data-gc-logo-kit]');
    if (!el) return { labels: {}, price: '', fee: 0, css: '' }; /* not kept: the kit may come later */
    var k = {};
    try { k = JSON.parse(el.textContent) || {}; } catch (e) {}
    KIT = { labels: k.labels || {}, price: k.price || '', fee: k.fee || 0, css: k.css || '' };
    return KIT;
  }

  /* ---------------------------------------------------------- the twin maps, by product id
     media ids are unique across the store, so a media id alone finds its entry too */
  var MAPS = Object.create(null);
  function register(pid, map) {
    if (!map || typeof map !== 'object') return;
    var m = MAPS[String(pid)] || (MAPS[String(pid)] = Object.create(null));
    for (var k in map) if (Object.prototype.hasOwnProperty.call(map, k)) m[k] = map[k];
  }
  function scanMaps() {
    [].forEach.call(doc.querySelectorAll('script[data-gc-logo-map]'), function (s) {
      if (s.__gcLogoRead) return;
      s.__gcLogoRead = true;
      try { register(s.getAttribute('data-gc-logo-map'), JSON.parse(s.textContent)); } catch (e) {}
    });
  }
  function entry(args) {
    var pid = args.length > 1 ? args[0] : null, mid = String(args.length > 1 ? args[1] : args[0]);
    scanMaps();
    if (pid != null && pid !== '') { var m = MAPS[String(pid)]; return (m && m[mid]) || null; }
    for (var p in MAPS) if (MAPS[p][mid]) return MAPS[p][mid];
    return null;
  }
  function twin() {
    var e = entry(arguments), u = typeof e === 'string' ? e : (e && e.u);
    return u ? (u.indexOf('//') === 0 ? 'https:' + u : u) : '';
  }
  function spot() {
    var e = entry(arguments);
    if (!e || typeof e !== 'object') return null;
    var x = parseFloat(e.x), y = parseFloat(e.y), w = parseFloat(e.w), r = parseFloat(e.r) || 0;
    return isFinite(x) && isFinite(y) && w > 0 ? { x: x, y: y, w: w, r: r } : null;
  }

  /* ---------------------------------------------------------- the one choice */
  var state = false, owner = null, fns = [];
  function isOn() { return owner ? !!owner.isOn() : state; }
  function changed() {
    var on = isOn();
    fns.slice().forEach(function (f) { try { f(on); } catch (e) {} });
    refreshAll();
    try { doc.dispatchEvent(new CustomEvent('gc:logo-change', { detail: { on: on } })); } catch (e) {}
  }
  var GCLogo = {
    isOn: isOn,
    set: function (on) {
      on = !!on;
      if (isOn() === on) return;
      if (owner) owner.toggle(); else { state = on; changed(); }
    },
    toggle: function () { GCLogo.set(!isOn()); },
    onChange: function (fn) {
      fns.push(fn);
      return function () { var i = fns.indexOf(fn); if (i >= 0) fns.splice(i, 1); };
    },
    own: function (o) { owner = o && typeof o.isOn === 'function' && typeof o.toggle === 'function' ? o : null; },
    changed: changed,
    twin: twin,
    spot: spot,
    register: register,
    kit: kit,
    tagItems: function (items) { try { return owner && owner.tag ? owner.tag(items) || 0 : 0; } catch (e) { return 0; } }
  };

  /* ---------------------------------------------------------- the stylesheet
     The layer's box is set first, so a layer never sits in the flow, and its parts stay hidden
     until gc-logo-spot.css (loaded without blocking the first paint) shows them. */
  function ensureCss() {
    if (!doc.getElementById('gc-lgs-critical')) {
      var s = doc.createElement('style');
      s.id = 'gc-lgs-critical';
      s.textContent = '.gc-lgs{position:absolute;left:0;top:0;width:100%;height:100%;z-index:2;pointer-events:none}.gc-lgs>*{position:absolute;visibility:hidden}';
      (doc.head || doc.documentElement).appendChild(s);
    }
    var url = kit().css;
    if (url && !doc.querySelector('link[href*="gc-logo-spot.css"]')) {
      var l = doc.createElement('link');
      l.rel = 'stylesheet';
      l.href = url;
      (doc.head || doc.documentElement).appendChild(l);
    }
  }

  /* ---------------------------------------------------------- the leather under the chip
     The photo is read once, small (48px wide), from a copy requested with CORS (the store's own
     /cdn/shop/ and cdn.shopify.com both allow it); a photo that cannot be read leaves the chip on
     its neutral glass (gc-logo-spot.css), which reads on light and dark alike. Kept per photo. */
  var GRIDS = Object.create(null);
  function plain(u) {
    try {
      var x = new URL(u, location.href);
      ['width', 'height', 'crop', 'pad_color'].forEach(function (k) { x.searchParams.delete(k); });
      return x;
    } catch (e) { return null; }
  }
  function gridKey(u) { var x = plain(u); return x ? x.host + x.pathname + x.search : String(u || ''); }
  function smallUrl(u) {
    var x = plain(u);
    if (!x) return u;
    if (x.pathname.indexOf('/cdn/shop/') === 0 || /(^|\.)cdn\.shopify\.com$/.test(x.host)) x.searchParams.set('width', '96');
    return x.href;
  }
  function loadGrid(src, done) {
    var key = gridKey(src), g = GRIDS[key];
    if (g && g.px) return done(g);
    if (g && g.bad) return done(null);
    if (g) { g.wait.push(done); return; }
    g = GRIDS[key] = { wait: [done] };
    var im = new Image();
    im.crossOrigin = 'anonymous';
    im.decoding = 'async';
    im.onload = function () {
      try {
        var W = 48, H = Math.max(1, Math.round(W * im.naturalHeight / (im.naturalWidth || 1)));
        var c = doc.createElement('canvas');
        c.width = W; c.height = H;
        var cx = c.getContext('2d');
        cx.drawImage(im, 0, 0, W, H);
        g.px = cx.getImageData(0, 0, W, H).data;
        g.w = W; g.h = H;
      } catch (e) { g.bad = true; }
      flush();
    };
    im.onerror = function () { g.bad = true; flush(); };
    function flush() { var w = g.wait; g.wait = []; w.forEach(function (f) { f(g.bad ? null : g); }); }
    im.src = smallUrl(src);
  }
  function lin(c) { return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
  function lum(r, g, b) { return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b); }
  var INK = lum(28 / 255, 27 / 255, 27 / 255); /* #1c1b1b, the dark words */
  /* the ink that gives its words more contrast over this part of the photo (fractions 0..1);
     the two tint alphas are the chip's in gc-logo-spot.css (.is-dark .10, .is-light .18: a ghost chip, Oct 2026) */
  function toneAt(g, x0, y0, x1, y1) {
    var X0 = Math.max(0, Math.floor(x0 * g.w)), X1 = Math.min(g.w, Math.ceil(x1 * g.w));
    var Y0 = Math.max(0, Math.floor(y0 * g.h)), Y1 = Math.min(g.h, Math.ceil(y1 * g.h));
    var r = 0, gr = 0, b = 0, n = 0;
    for (var y = Y0; y < Y1; y++) {
      for (var x = X0; x < X1; x++) {
        var i = (y * g.w + x) * 4;
        r += g.px[i]; gr += g.px[i + 1]; b += g.px[i + 2]; n++;
      }
    }
    if (!n) return '';
    r /= n * 255; gr /= n * 255; b /= n * 255;
    var dark = lum(r * 0.9, gr * 0.9, b * 0.9);
    var light = lum(r * 0.82 + 0.18, gr * 0.82 + 0.18, b * 0.82 + 0.18);
    var onDark = 1.05 / (dark + 0.05), onLight = (light + 0.05) / (INK + 0.05);
    return onDark >= 4.5 || onDark >= onLight ? 'dark' : 'light';
  }

  /* ---------------------------------------------------------- the spots */
  var mounts = [], live = null, down = null, ro = null, io = null, queued = null, wired = false;

  function wire() {
    if (wired) return;
    wired = true;
    if (typeof ResizeObserver === 'function') {
      ro = new ResizeObserver(function (entries) {
        var t = entries.map(function (en) { return en.target; });
        queue(mounts.filter(function (m) { return t.indexOf(m.img) >= 0 || t.indexOf(m.host) >= 0; }));
      });
    }
    if (typeof IntersectionObserver === 'function') {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var m = en.target.__gcSpot;
          if (!m) return;
          m.seen = en.isIntersecting;
          if (m.seen) { queue([m]); readTone(m); }
        });
      });
    }
    /* pointerdown in the capture phase: a slider's own handlers must not hide it from us */
    doc.addEventListener('pointerdown', function (e) {
      var s = e.target.closest && e.target.closest('[data-gc-logo-spot-btn]');
      down = s ? { s: s, x: e.clientX, y: e.clientY } : null;
    }, true);
    /* a window resize refits the spots on screen (a photo whose box really changed is reported by
       the ResizeObserver; iOS fires resize whenever its toolbar folds away during a scroll) */
    window.addEventListener('resize', function () { queue(mounts.filter(function (m) { return m.seen !== false; })); }, { passive: true });
    window.addEventListener('load', function () { queue(mounts.slice()); });
    /* the status line is in the page before the first press: a live region added just before it
       speaks is often not read (VoiceOver on iOS) */
    live = doc.createElement('span');
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('data-gc-logo-status', '');
    live.style.cssText = 'position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap';
    doc.body.appendChild(live);
  }

  function labelsOf(m) {
    var k = kit().labels, o = m.opts.labels || {};
    return { inside: o.inside || k.inside || '', aria: o.aria || k.aria || '', on: o.on || k.on || '', off: o.off || k.off || '' };
  }
  function onOf(m) { try { return m.opts.isOn ? !!m.opts.isOn() : isOn(); } catch (e) { return false; } }
  function priceOf(m) {
    var p = m.opts.price;
    try { p = typeof p === 'function' ? p() : p; } catch (e) { p = ''; }
    if (p == null) p = kit().price;
    return String(p || '').replace(/\s+/g, ' ').trim();
  }
  /* One name for both states: the spot is a toggle (aria-pressed), and a name that changed beside
     "pressed" would read as two different buttons. It starts with the words the spot shows ("Logo
     inside"), then says what it is and costs ("Minimalist logo, $25"); the chip's x is only a sign. */
  function nameOf(L, pr) {
    var n = String(L.aria || L.inside || '');
    n = pr ? n.split('[price]').join(pr) : n.replace(/[\s,，、،:：]*\[price\]/g, '');
    var w = String(L.inside || '').trim();
    if (w && n.toLowerCase().indexOf(w.toLowerCase()) < 0) n = w + ' ' + n;
    return n.trim();
  }
  function put(el, a, v) { if (el.getAttribute(a) !== v) el.setAttribute(a, v); }
  function text(el, v) { if (el.textContent !== v) el.textContent = v; }

  function render(m) {
    if (m.gone) return;
    var L = labelsOf(m), on = onOf(m), pr = priceOf(m);
    m.el.classList.toggle('is-on', on);
    m.el.classList.toggle('is-mute', !L.inside); /* no words: nothing to show */
    [].forEach.call(m.el.querySelectorAll('[data-t="inside"]'), function (t) { text(t, L.inside); });
    text(m.priceEl, pr ? '+' + pr : '');
    m.priceEl.hidden = !pr;
    put(m.button, 'aria-label', nameOf(L, pr));
    put(m.button, 'aria-pressed', on ? 'true' : 'false');
    if (m.on !== on) { m.on = on; m.chipRect = null; if (on) applyTone(m); }
    queue([m]);
  }

  function setPos(m, p) {
    m.pos = { x: +p.x || 50, y: +p.y || 83, w: +p.w || 20, r: +p.r || 0 };
    var s = m.el.style;
    s.setProperty('--gc-lgs-x', m.pos.x + '%');
    s.setProperty('--gc-lgs-y', m.pos.y + '%');
    s.setProperty('--gc-lgs-w', m.pos.w + '%');
    s.setProperty('--gc-lgs-r', m.pos.r + 'deg');
    m.key = '';
    m.chipRect = null;
  }

  function mount(host, img, pos, opts) {
    if (!host || !img || !pos) return null;
    opts = opts || {};
    ensureCss();
    wire();
    try { if (window.getComputedStyle(host).position === 'static') host.style.position = 'relative'; } catch (e) {}
    var layer = doc.createElement('div');
    layer.className = 'gc-lgs';
    layer.setAttribute('data-gc-logo-spot', '');
    layer.innerHTML =
      '<button type="button" class="gc-lgs__spot" data-gc-logo-spot-btn aria-pressed="false">' +
        '<span class="gc-lgs__rim" aria-hidden="true"></span>' +
        '<span class="gc-lgs__pill" aria-hidden="true">' + ICON_OFFER + '<span class="gc-lgs__t" data-t="inside"></span><span class="gc-lgs__price" data-t="price"></span></span>' +
        '<span class="gc-lgs__chip" aria-hidden="true">' + ICON_IN + '<span class="gc-lgs__t" data-t="inside"></span>' + ICON_BACK + '</span>' +
      '</button>';
    var m = {
      host: host, img: img, opts: opts, el: layer,
      button: layer.querySelector('[data-gc-logo-spot-btn]'),
      pill: layer.querySelector('.gc-lgs__pill'),
      chip: layer.querySelector('.gc-lgs__chip'),
      priceEl: layer.querySelector('[data-t="price"]'),
      key: '', sized: false, caps: '', on: null, seen: null, tone: ''
    };
    layer.__gcSpot = m;
    setPos(m, pos);
    if (opts.tone === 'dark' || opts.tone === 'light') setTone(m, opts.tone);
    m.button.addEventListener('click', function (e) { press(m, e); });
    /* every load: a lazy photo arriving gives the layer a size (is-sized), a src change refits it
       and reads the leather of the photo now shown */
    m.onLoad = function () { queue([m]); readTone(m); };
    img.addEventListener('load', m.onLoad);
    if (typeof MutationObserver === 'function') {
      m.mo = new MutationObserver(function () { queue([m]); });
      m.mo.observe(img, { attributes: true, attributeFilter: ['src', 'srcset', 'sizes', 'width', 'height', 'style', 'class'] });
    }
    host.appendChild(layer);
    if (ro) { ro.observe(img); ro.observe(host); }
    if (io) io.observe(layer); else m.seen = true;
    mounts.push(m);
    var handle = {
      el: layer,
      button: m.button,
      refresh: function () { render(m); },
      update: function (p) { if (p) { setPos(m, p); render(m); readTone(m); } },
      destroy: function () { destroy(m); }
    };
    m.handle = handle;
    render(m);
    layer.classList.add('is-ready');
    if (!io) readTone(m);
    return handle;
  }

  function destroy(m) {
    if (m.gone) return;
    m.gone = true;
    m.img.removeEventListener('load', m.onLoad);
    if (m.mo) m.mo.disconnect();
    if (ro) { try { ro.unobserve(m.img); } catch (e) {} if (!mounts.some(function (o) { return o !== m && o.host === m.host; })) try { ro.unobserve(m.host); } catch (e) {} }
    if (io) try { io.unobserve(m.el); } catch (e) {}
    if (m.el.parentNode) m.el.parentNode.removeChild(m.el);
    var i = mounts.indexOf(m);
    if (i >= 0) mounts.splice(i, 1);
  }

  function refreshAll() {
    mounts.slice().forEach(function (m) {
      /* a layer whose photo has left the page for good (a rebuilt gallery) */
      if (!m.el.isConnected && !m.img.isConnected) { destroy(m); return; }
      render(m);
    });
  }

  /* ---------------------------------------------------------- where the picture is
     The layer is the picture itself: the image box less any letterboxing (object-fit: contain) or
     plus any crop (cover), so x/y/w land on the wordmark whatever the CSS does to the image. Layout
     offsets (not screen rectangles), so a scaled or sliding parent does not throw it off. */
  function boxIn(host, el) {
    var x = 0, y = 0, n = el;
    while (n && n !== host) {
      x += n.offsetLeft; y += n.offsetTop;
      var p = n.offsetParent;
      if (!p || (p !== host && !host.contains(p))) { n = null; break; }
      if (p !== host) { x += p.clientLeft; y += p.clientTop; }
      n = p;
    }
    if (n === host) return { x: x, y: y };
    var hr = host.getBoundingClientRect(), er = el.getBoundingClientRect();
    var k = host.offsetWidth ? hr.width / host.offsetWidth : 1;
    return { x: (er.left - hr.left) / (k || 1) - host.clientLeft, y: (er.top - hr.top) / (k || 1) - host.clientTop };
  }
  function offset(v, free) {
    v = String(v || '50%');
    return /%$/.test(v) ? free * parseFloat(v) / 100 : /px$/.test(v) ? parseFloat(v) : free / 2;
  }
  /* What shows of the photo, top to bottom: every box up to the section that hides its overflow
     cuts it (a slider's viewport; on the product page at 501-767px the gallery wrapper is 500px
     tall over a 700px photo and hides the wordmark). A box that scrolls is left out: what it hides
     comes back with a scroll. Sideways is left out too: the slides off screen are hidden anyway. */
  function clipY(img) {
    var top = -Infinity, bottom = Infinity;
    for (var el = img.parentElement; el && el !== doc.body; el = el.parentElement) {
      if (el.classList.contains('shopify-section')) break;
      var o = window.getComputedStyle(el).overflowY;
      if (o !== 'hidden' && o !== 'clip') continue;
      var r = el.getBoundingClientRect();
      top = Math.max(top, r.top);
      bottom = Math.min(bottom, r.bottom);
    }
    return { top: top, bottom: bottom };
  }
  function measure(m) {
    var img = m.img;
    if (m.gone || !m.el.isConnected || !img.isConnected) return null;
    /* nothing to point at until the photo has first arrived (a lazy one is 0x0, or a blank box).
       Once it has, the spot stays where it is while a src change reloads the photo: Chrome reports
       no size for that moment, and hiding the button just pressed would also drop the focus. */
    if (!img.naturalWidth || !img.offsetWidth || !img.offsetHeight) return m.sized ? null : { sized: false, key: '-' };
    var o = boxIn(m.host, img), x = o.x, y = o.y, w = img.offsetWidth, h = img.offsetHeight;
    var cs = window.getComputedStyle(img);
    var nw = img.naturalWidth, nh = img.naturalHeight;
    var mode = cs.objectFit || cs.getPropertyValue('object-fit') || '';
    if (nw && nh && /^(contain|cover|scale-down)$/.test(mode)) {
      var k = mode === 'cover' ? Math.max(w / nw, h / nh) : Math.min(w / nw, h / nh);
      if (mode === 'scale-down') k = Math.min(k, 1);
      var cw = nw * k, ch = nh * k;
      var op = String(cs.objectPosition || '50% 50%').split(/\s+/);
      x += offset(op[0], w - cw);
      y += offset(op[1], h - ch);
      w = cw; h = ch;
    }
    var vt = 0, vb = h;
    if (m.opts.clip !== false) {
      var c = clipY(img), top = img.getBoundingClientRect().top + (y - o.y); /* the picture's top on screen */
      vt = Math.max(0, c.top - top);
      vb = Math.min(h, c.bottom - top);
    }
    var ph = m.pill.offsetHeight > 20 ? m.pill.offsetHeight : 32;
    var on = m.el.classList.contains('is-on');
    var chw = on ? m.chip.offsetWidth : 0, chh = on ? m.chip.offsetHeight : 0;
    return {
      sized: true, x: x, y: y, w: w, h: h, vt: vt, vb: vb, ph: ph, chw: chw, chh: chh,
      key: [x, y, w, h, vt, vb, ph, chw, chh].map(function (n) { return Math.round(n * 2) / 2; }).join(',')
    };
  }
  /* writes only; true when a width cap moved, so the pill or chip is measured again */
  function place(m, g) {
    var layer = m.el;
    m.key = g.key;
    layer.classList.toggle('is-sized', g.sized);
    if (!g.sized) return false;
    m.sized = true;
    var p = m.pos, w = g.w, h = g.h;
    layer.style.left = g.x + 'px'; layer.style.top = g.y + 'px';
    layer.style.width = w + 'px'; layer.style.height = h + 'px';
    /* a tilted frame reaches lower than a level one: the pill drops by the difference */
    var fw = w * p.w / 100 * WIDEN, fh = fw / ASPECT, a = Math.abs(p.r) * Math.PI / 180;
    var drop = Math.max(0, fw / 2 * Math.sin(a) + fh / 2 * (Math.cos(a) - 1));
    layer.style.setProperty('--gc-lgs-drop', drop.toFixed(1) + 'px');
    /* the pill (on the photo's light ground) may use most of the photo's width; the chip stays on the
       case: about two wordmarks wide is the leather and the case's dark edge on every one of these
       photos (2.2, so its round ends may sit on that edge). A longer phrase wraps to a second line
       rather than spill onto the white ground (on a 320px phone, in one or two languages). */
    var maxw = Math.max(120, Math.round(w * 0.9));
    var chipw = Math.max(110, Math.min(maxw, Math.round(w * p.w / 100 * 2.2)));
    layer.style.setProperty('--gc-lgs-maxw', maxw + 'px');
    layer.style.setProperty('--gc-lgs-chipw', chipw + 'px');
    /* the wordmark cut off by the gallery box: no spot, rather than a frame around half a logo */
    var cx = w * p.x / 100, cy = h * p.y / 100;
    layer.classList.toggle('is-out', cy - fh / 2 < g.vt || cy + fh / 2 > g.vb);
    /* no room for the pill under the wordmark in what shows of the photo: above it, if it fits */
    var below = cy + fh / 2 + drop + 6 + g.ph <= g.vb - 4;
    var above = cy - fh / 2 - drop - 6 - g.ph >= g.vt + 4;
    layer.classList.toggle('is-above', !below && above);
    /* the chip's own footprint on the photo, for its glass */
    if (g.chw && g.chh) {
      m.chipRect = [(cx - g.chw / 2) / w, (cy - g.chh / 2) / h, (cx + g.chw / 2) / w, (cy + g.chh / 2) / h];
      applyTone(m);
    }
    var caps = maxw + '/' + chipw;
    if (m.caps === caps) return false;
    m.caps = caps;
    return true;
  }
  function fit(list) {
    var due = [];
    list.forEach(function (m) {
      var g = measure(m);
      if (g && g.key !== m.key) due.push([m, g]); /* unchanged since the last fit: left alone */
    });
    var again = due.filter(function (d) { return place(d[0], d[1]); }).map(function (d) { return d[0]; });
    if (again.length) queue(again);
  }
  /* spots due for a fit, done together on the next frame: every one is measured first, then all are
     placed, so a resize costs one layout, not one per photo */
  function queue(list) {
    if (!list || !list.length) return;
    if (!queued) {
      queued = [];
      var run = function () { var l = queued; queued = null; fit(l); };
      if (window.requestAnimationFrame) requestAnimationFrame(run); else setTimeout(run, 16);
    }
    list.forEach(function (m) { if (queued.indexOf(m) < 0) queued.push(m); });
  }

  /* ---------------------------------------------------------- the chip's glass */
  function setTone(m, t) {
    if (m.tone === t) return;
    m.tone = t;
    m.el.classList.toggle('is-dark', t === 'dark');
    m.el.classList.toggle('is-light', t === 'light');
  }
  function applyTone(m) {
    if (m.opts.tone === 'dark' || m.opts.tone === 'light' || !m.grid) return;
    var r = m.chipRect, p = m.pos;
    if (!r) {
      /* before the chip has been measured: about its size, centred on the wordmark */
      var hw = p.w * 0.8 / 100, hh = p.w * WIDEN / ASPECT * 1.4 / 100;
      r = [p.x / 100 - hw, p.y / 100 - hh, p.x / 100 + hw, p.y / 100 + hh];
    }
    var t = toneAt(m.grid, r[0], r[1], r[2], r[3]);
    if (t) setTone(m, t);
  }
  function readTone(m) {
    if (m.gone || m.opts.tone === 'dark' || m.opts.tone === 'light') return;
    if (io && !m.seen) return; /* read when it comes on screen */
    var img = m.img, src = img.currentSrc || img.getAttribute('src') || '';
    if (!src || !img.naturalWidth || /^data:/.test(src)) return;
    var key = gridKey(src);
    if (m.gridKey === key) return;
    loadGrid(src, function (g) {
      if (gridKey(img.currentSrc || img.getAttribute('src') || '') !== key) return; /* moved on meanwhile */
      m.gridKey = key;
      if (!g) return; /* unreadable: the tone it had stays (a twin swap keeps the same leather) */
      m.grid = g;
      applyTone(m);
    });
  }

  /* ---------------------------------------------------------- a press */
  function press(m, e) {
    var s = m.button;
    var swiped = down && down.s === s && e.detail !== 0 &&
      (Math.abs(e.clientX - down.x) > MOVE || Math.abs(e.clientY - down.y) > MOVE);
    down = null;
    /* the click goes no further either way: on a tap the slide underneath would open the zoom, and
       after a swipe the slider has already had the gesture */
    e.preventDefault();
    e.stopPropagation();
    if (swiped) return;
    var was = onOf(m);
    try { if (m.opts.toggle) m.opts.toggle(); else GCLogo.toggle(); } catch (err) {}
    render(m);
    var now = onOf(m);
    if (now === was) now = !was; /* a toggle that settles later: say what it is about to be */
    /* one voice per press. A spot that holds the focus (after Enter or Space, and after a tap in the
       browsers that focus a pressed button) has its new pressed state read out by the screen reader
       as aria-pressed turns, so the status line would say it twice; it speaks when the spot is not
       focused: a mouse click on a slider that takes the focus, and a tap in Safari, which never
       focuses a button. */
    if (doc.activeElement !== s) say(now ? labelsOf(m).on : labelsOf(m).off);
    if (typeof m.opts.onToggle === 'function') { try { m.opts.onToggle(now); } catch (err) {} }
  }
  function say(t) {
    if (!t || !live) return;
    live.textContent = '';
    setTimeout(function () { live.textContent = t; }, 80);
  }

  window.GCLogo = GCLogo;
  window.GCLogoSpot = {
    mount: function (host, img, pos, opts) {
      var L = labelsOf({ opts: opts || {} });
      if (!L.inside) return null; /* no words (no kit, no labels): no spot that says nothing */
      return mount(host, img, pos, opts);
    },
    refreshAll: refreshAll,
    get CSS_URL() { return kit().css; }
  };
})();
